<script lang="ts">
	import { browser } from '$app/environment';
	import { Theme } from '@tools';
	import Color from '@tools/Color';
	import { expoOut } from 'svelte/easing';
	import { tweened } from 'svelte/motion';

	let {
		ratio = $bindable(0),
		color = Theme.current?.primary,
		animateOnHover = true,
		hoverTrigger
	}: {
		ratio: number;
		color?: string | Color;
		animateOnHover?: boolean;
		hoverTrigger?: HTMLElement;
	} = $props();

	let easedHoverRatio = tweened(0, { easing: expoOut });
	const durationIn = 300;
	const durationOut = 850;
	let canvas: HTMLCanvasElement | undefined = $state(undefined);
	let w = $state(0);
	let h = $state(0);
	let pixelRatio = $state(browser ? Math.min(window.devicePixelRatio, 2) : 1);
	let width = $derived(w * pixelRatio);
	let height = $derived(h * pixelRatio);

	const mouseEnter = () => {
		easedHoverRatio.set(1, { duration: durationIn });
	};
	const mouseLeave = () => {
		easedHoverRatio.set(0, { duration: durationOut });
	};
	const focus = () => {
		easedHoverRatio.set(1, { duration: durationIn });
	};
	const blur = () => {
		easedHoverRatio.set(0, { duration: durationOut });
	};

	$effect(() => {
		if (animateOnHover) {
			ratio = $easedHoverRatio;
		}
	});

	$effect(() => {
		const ctx = canvas?.getContext('2d');
		if (!ctx) return;

		let max: number = Math.max(width, height);
		let maxOffset: number = Math.round(max * 0.3);

		let offsetSize: number = maxOffset - maxOffset * ratio;
		let overflow = 2; // To fix sub-pixel edges

		ctx.clearRect(0, 0, width, height);
		ctx.fillStyle = color ? `${color}` : '#F0F0F0';
		ctx.beginPath();

		const iteration = 80;

		ctx.moveTo(width / 2, height / 2);

		for (let i = 0; i < iteration; i += 1) {
			let angle: number = ((0 + (360 / (iteration - 1)) * i) * Math.PI) / 180;
			let xPos: number = (width / 2 + offsetSize) * Math.cos(angle) + width / 2;
			let yPos: number = (height / 2 + offsetSize) * Math.sin(angle) + height / 2;

			if (xPos < 0) {
				xPos = 0;
			} else if (xPos > width) {
				xPos = width;
			}

			if (yPos < 0) {
				yPos = 0;
			} else if (yPos > height) {
				yPos = height;
			}

			ctx.lineTo(xPos, yPos);
		}

		ctx.lineTo(width + overflow, overflow * -1); // upper right
		ctx.lineTo(overflow * -1, overflow * -1); // upper left
		ctx.lineTo(overflow * -1, height + overflow); // lower left
		ctx.lineTo(width + overflow, height + overflow); // lower right
		ctx.lineTo(width + overflow, height / 2); // close

		ctx.fill();
	});

	$effect(() => {
		if (!hoverTrigger) return;
		hoverTrigger.addEventListener('mouseenter', mouseEnter);
		hoverTrigger.addEventListener('mouseleave', mouseLeave);
		hoverTrigger.addEventListener('focusin', focus);
		hoverTrigger.addEventListener('focusout', blur);

		return () => {
			hoverTrigger?.removeEventListener('mouseenter', mouseEnter);
			hoverTrigger?.removeEventListener('mouseleave', mouseLeave);
			hoverTrigger?.removeEventListener('focusin', focus);
			hoverTrigger?.removeEventListener('focusout', blur);
		};
	});
</script>

<canvas
	class="OvalEffect"
	bind:offsetHeight={h}
	bind:offsetWidth={w}
	aria-hidden="true"
	bind:this={canvas}
	{width}
	{height}
></canvas>

<style lang="scss">
	canvas {
		display: block;
		position: absolute;
		top: -1px;
		left: -1px;
		width: calc(100% + 2px);
		height: calc(100% + 2px);
		pointer-events: none;
		z-index: 3;
	}
</style>
